import { PureComponent, ReactNode } from 'react';
import { TreatmentPlan } from '../../../../../api/getTreatmentPlans';
import { TreatmentPlansFetchState } from './PPTreatmentPlan';
import { DentalProcedure } from '../../../../../store/types/prescription';
import getTreatmentPlan from '../../../../../api/getTreatmentPlan';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import { track } from '../../../../../tracking';
import { Popover, notification } from 'antd';
import WorkDoneTable from './WorkDoneTable';
import { formattedNumber } from '../../../../../pages/utils';

interface Props {
    treatmentPlan?: TreatmentPlan;
    treatmentBillingModalToggleHandler: () => void;
}

interface State {
    treatmentPlanFetchState?: TreatmentPlansFetchState;
    treatmentProcedures?: DentalProcedure[];
}

class OngoingPlansForRightPanel extends PureComponent<Props, State> {
    state: State = {};

    componentDidMount = async () => {
        const { fetchTreatmentPlan } = this;
        fetchTreatmentPlan();
    };

    fetchTreatmentPlan = async () => {
        const { treatmentPlan } = this.props;
        if (!treatmentPlan) return;
        const { plan_id } = treatmentPlan;
        this.setState({
            treatmentPlanFetchState: 'loading',
            treatmentProcedures: undefined,
        });
        try {
            const { success, treatmentProcedures, error = '' } = await getTreatmentPlan(
                plan_id,
                '',
            );
            if (success) {
                this.setState({
                    treatmentPlanFetchState: 'success',
                    treatmentProcedures: treatmentProcedures,
                });
            } else {
                this.setState({
                    treatmentPlanFetchState: 'error',
                });
                captureErrorString(error);
                track('ERROR', {
                    on: 'getTreatmentPlan',
                    error: error,
                });
                notification.error({
                    message: 'Error in fetching treatment plan',
                    duration: 5,
                });
            }
        } catch (error) {
            this.setState({
                treatmentPlanFetchState: 'error',
            });
            captureError(error as Error);
            track('ERROR', {
                on: 'getTreatmentPlan',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in fetching treatment plan',
                duration: 5,
            });
        }
    };

    render(): ReactNode {
        const { treatmentPlan } = this.props;
        const { treatmentPlanFetchState, treatmentProcedures } = this.state;
        const { fetchTreatmentPlan } = this;

        return treatmentPlanFetchState === 'loading' ? (
            <div
                className="m-16 flex-1 overflow-auto flex items-center justify-center bg-white"
                style={{
                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                }}
            >
                <svg
                    className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                </svg>
            </div>
        ) : treatmentPlanFetchState === 'error' ? (
            <div
                className="m-16 flex-1 overflow-auto flex flex-col items-center justify-center bg-white space-y-12"
                style={{
                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                }}
            >
                <div className="flex items-center justify-center w-80 h-80">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 576 512"
                        className="w-80 h-80 fill-darwin-accent-bright-blue-800 opacity-70"
                    >
                        <path d="M248.747 204.705l6.588 112c.373 6.343 5.626 11.295 11.979 11.295h41.37a12 12 0 0 0 11.979-11.295l6.588-112c.405-6.893-5.075-12.705-11.979-12.705h-54.547c-6.903 0-12.383 5.812-11.978 12.705zM330 384c0 23.196-18.804 42-42 42s-42-18.804-42-42 18.804-42 42-42 42 18.804 42 42zm-.423-360.015c-18.433-31.951-64.687-32.009-83.154 0L6.477 440.013C-11.945 471.946 11.118 512 48.054 512H527.94c36.865 0 60.035-39.993 41.577-71.987L329.577 23.985zM53.191 455.002L282.803 57.008c2.309-4.002 8.085-4.002 10.394 0l229.612 397.993c2.308 4-.579 8.998-5.197 8.998H58.388c-4.617.001-7.504-4.997-5.197-8.997z" />
                    </svg>
                </div>
                <div className="space-y-4">
                    <p className="text-darwin-neutrals-1000 font-700 leading-5 text-center">
                        Failed to load work done for {treatmentPlan?.name} plan
                    </p>
                    <p className="text-darwin-neutrals-600 text-13 leading-5 text-center">
                        <button
                            onClick={fetchTreatmentPlan}
                            className="font-700 underline text-darwin-accent-bright-blue-800"
                        >
                            Refresh
                        </button>{' '}
                        the page to try again.
                    </p>
                </div>
            </div>
        ) : (
            <div className="p-16 flex-1 flex flex-col overflow-auto bg-darwin-neutrals-50 space-y-10">
                <p className="text-red-primary">
                    * You can add workdone only from Rx, please start visit
                </p>
                {treatmentPlan?.services.map((i) => {
                    const proceduresArray = Array.isArray(treatmentProcedures)
                        ? treatmentProcedures
                        : [];
                    const filteredTreatmentProcedures = proceduresArray.filter(
                        (j) => j.service_id === i.id,
                    );
                    const { name, teeth_ids, surfaces, qty, mrp, discount, amount, remarks } = i;
                    const teethAndSurfaces = [
                        teeth_ids?.map((tooth) => `T${tooth}`).join(', '),
                        surfaces?.map((surface) => surface.name).join(', '),
                    ]
                        .filter(Boolean)
                        .join(' | ');
                    return (
                        <div
                            className="rounded-8 space-y-12 px-16 pt-12 pb-16 bg-white"
                            style={{
                                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                            }}
                        >
                            <div className="flex items-center space-x-8">
                                <div
                                    className="w-28 h-28 flex items-center justify-center rounded-4"
                                    style={{ background: '#E8E8E8' }}
                                >
                                    <svg
                                        className="w-16 h-13 fill-darwin-neutral_new-800"
                                        viewBox="0 0 16 13"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M0.6875 3.375C0.6875 2.0293 1.7793 0.9375 3.125 0.9375H12.875C14.2207 0.9375 15.3125 2.0293 15.3125 3.375V9.875C15.3125 11.2207 14.2207 12.3125 12.875 12.3125H3.125C1.7793 12.3125 0.6875 11.2207 0.6875 9.875V3.375ZM5.15625 4.59375V6.01562C5.15625 6.37109 5.41016 6.625 5.76562 6.625H6.98438C7.31445 6.625 7.59375 6.37109 7.59375 6.01562V4.59375C7.59375 3.93359 7.03516 3.375 6.375 3.375C5.68945 3.375 5.15625 3.93359 5.15625 4.59375ZM9.625 3.375C8.93945 3.375 8.40625 3.93359 8.40625 4.59375V6.01562C8.40625 6.37109 8.66016 6.625 9.01562 6.625H10.2344C10.5645 6.625 10.8438 6.37109 10.8438 6.01562V4.59375C10.8438 3.93359 10.2852 3.375 9.625 3.375ZM1.90625 5.40625V6.01562C1.90625 6.37109 2.16016 6.625 2.51562 6.625H3.73438C4.06445 6.625 4.34375 6.37109 4.34375 6.01562V5.40625C4.34375 4.74609 3.78516 4.1875 3.125 4.1875C2.43945 4.1875 1.90625 4.74609 1.90625 5.40625ZM3.125 9.875C3.78516 9.875 4.34375 9.3418 4.34375 8.65625V8.04688C4.34375 7.7168 4.06445 7.4375 3.73438 7.4375H2.51562C2.16016 7.4375 1.90625 7.7168 1.90625 8.04688V8.65625C1.90625 9.3418 2.43945 9.875 3.125 9.875ZM5.15625 8.65625C5.15625 9.3418 5.68945 9.875 6.375 9.875C7.03516 9.875 7.59375 9.3418 7.59375 8.65625V8.04688C7.59375 7.7168 7.31445 7.4375 6.98438 7.4375H5.76562C5.41016 7.4375 5.15625 7.7168 5.15625 8.04688V8.65625ZM9.625 9.875C10.2852 9.875 10.8438 9.3418 10.8438 8.65625V8.04688C10.8438 7.7168 10.5645 7.4375 10.2344 7.4375H9.01562C8.66016 7.4375 8.40625 7.7168 8.40625 8.04688V8.65625C8.40625 9.3418 8.93945 9.875 9.625 9.875ZM11.6562 5.40625V6.01562C11.6562 6.37109 11.9102 6.625 12.2656 6.625H13.4844C13.8145 6.625 14.0938 6.37109 14.0938 6.01562V5.40625C14.0938 4.74609 13.5352 4.1875 12.875 4.1875C12.1895 4.1875 11.6562 4.74609 11.6562 5.40625ZM12.875 9.875C13.5352 9.875 14.0938 9.3418 14.0938 8.65625V8.04688C14.0938 7.7168 13.8145 7.4375 13.4844 7.4375H12.2656C11.9102 7.4375 11.6562 7.7168 11.6562 8.04688V8.65625C11.6562 9.3418 12.1895 9.875 12.875 9.875Z" />
                                    </svg>
                                </div>
                                <p className="text-16 font-700 text-darwin-neutral-1000">{name}</p>
                                {teethAndSurfaces && (
                                    <p className="text-13 text-darwin-neutral_new-800">
                                        ({teethAndSurfaces})
                                    </p>
                                )}
                                <div className="pt-2">
                                    <Popover
                                        placement="right"
                                        title={
                                            <p className="font-700 select-none">Service details</p>
                                        }
                                        content={
                                            <div className="space-y-4 select-none overflow-auto max-h-328 max-w-400">
                                                <div className="flex items-start justify-between">
                                                    <p className="flex-1">Qty</p>
                                                    <p className="text-right w-80">{qty}</p>
                                                </div>
                                                <div className="flex items-start justify-between">
                                                    <p className="flex-1">Mrp</p>
                                                    <p className="text-right w-80">
                                                        ₹{formattedNumber(mrp)}
                                                    </p>
                                                </div>
                                                <div className="flex items-start justify-between">
                                                    <p className="flex-1">Discount</p>
                                                    <p className="text-right w-80">{discount}%</p>
                                                </div>
                                                <div className="flex items-start justify-between">
                                                    <p className="flex-1">Amount</p>
                                                    <p className="text-right w-80">
                                                        ₹{formattedNumber(amount)}
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        trigger="hover"
                                        overlayClassName="max-w-328"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-14 h-14 fill-darwin-neutral_new-800"
                                            viewBox="0 0 14 14"
                                        >
                                            <path d="M7 1.34375C5.0957 1.34375 3.36914 2.35938 2.4043 3.98438C1.46484 5.63477 1.46484 7.64062 2.4043 9.26562C3.36914 10.916 5.0957 11.9062 7 11.9062C8.87891 11.9062 10.6055 10.916 11.5703 9.26562C12.5098 7.64062 12.5098 5.63477 11.5703 3.98438C10.6055 2.35938 8.87891 1.34375 7 1.34375ZM7 13.125C4.66406 13.125 2.53125 11.9062 1.36328 9.875C0.195312 7.86914 0.195312 5.40625 1.36328 3.375C2.53125 1.36914 4.66406 0.125 7 0.125C9.31055 0.125 11.4434 1.36914 12.6113 3.375C13.7793 5.40625 13.7793 7.86914 12.6113 9.875C11.4434 11.9062 9.31055 13.125 7 13.125ZM5.98438 8.65625H6.59375V7.03125H5.98438C5.62891 7.03125 5.375 6.77734 5.375 6.42188C5.375 6.0918 5.62891 5.8125 5.98438 5.8125H7.20312C7.5332 5.8125 7.8125 6.0918 7.8125 6.42188V8.65625H8.01562C8.3457 8.65625 8.625 8.93555 8.625 9.26562C8.625 9.62109 8.3457 9.875 8.01562 9.875H5.98438C5.62891 9.875 5.375 9.62109 5.375 9.26562C5.375 8.93555 5.62891 8.65625 5.98438 8.65625ZM7 5C6.54297 5 6.1875 4.64453 6.1875 4.1875C6.1875 3.75586 6.54297 3.375 7 3.375C7.43164 3.375 7.8125 3.75586 7.8125 4.1875C7.8125 4.64453 7.43164 5 7 5Z" />
                                        </svg>
                                    </Popover>
                                </div>
                            </div>
                            {remarks && (
                                <p className="text-13 text-darwin-neutral_new-800">
                                    Remarks: {remarks}
                                </p>
                            )}
                            <WorkDoneTable treatmentProcedures={filteredTreatmentProcedures} />
                        </div>
                    );
                })}
            </div>
        );
    }
}

export default OngoingPlansForRightPanel;
